import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const API_BASE = `${BACKEND_URL}/api`;

// --- Token storage (Bearer fallback) ---------------------------------------
// Auth primarily uses httpOnly cookies, but Safari/iOS blocks third-party
// cookies when the API is cross-site, which logs users out every visit. To
// guarantee persistent sessions regardless of cookie policy, we ALSO keep the
// JWT in localStorage and send it as an Authorization: Bearer header. The
// backend already accepts either the cookie or the Bearer header.
const ACCESS_KEY = "zb_access_token";
const REFRESH_KEY = "zb_refresh_token";

export function setTokens(access, refresh) {
  if (access) localStorage.setItem(ACCESS_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}
export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}
export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY);
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach the stored access token on every request (first-party localStorage,
// not subject to third-party cookie blocking).
api.interceptors.request.use((config) => {
  const t = localStorage.getItem(ACCESS_KEY);
  if (t) config.headers = { ...config.headers, Authorization: `Bearer ${t}` };
  return config;
});

// --- Silent session refresh -------------------------------------------------
// Any authenticated request that returns 401 triggers a single attempt to
// mint a fresh access cookie via `/auth/refresh` (which reads the long-lived
// refresh cookie). On success we replay the original request, so the user
// never sees a "Not authenticated" toast mid-task. Concurrent 401s coalesce
// onto the same refresh promise.
let _refreshPromise = null;

api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const { config, response } = error;
    // Bail when no response (network error), no config (shouldn't happen),
    // already retried, or the failing call is the refresh/login endpoint
    // itself (retrying those would loop or mask user-facing errors).
    if (
      !response ||
      !config ||
      config._retried ||
      (config.url || "").endsWith("/auth/refresh") ||
      (config.url || "").endsWith("/auth/login")
    ) {
      return Promise.reject(error);
    }
    if (response.status !== 401) return Promise.reject(error);

    try {
      if (!_refreshPromise) {
        const rt = localStorage.getItem(REFRESH_KEY);
        _refreshPromise = api
          .post("/auth/refresh", rt ? { refresh_token: rt } : {})
          .then(({ data }) => {
            if (data && data.access_token) setTokens(data.access_token, data.refresh_token);
            return data;
          })
          .finally(() => {
            _refreshPromise = null;
          });
      }
      await _refreshPromise;
      config._retried = true;
      return api.request(config);
    } catch (_) {
      clearTokens();
      return Promise.reject(error);
    }
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "حدث خطأ غير متوقع";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
